// Account Settings → Data & Storage, Connected Accounts, Danger Zone (1.27.70).
//
// Data & Storage uses the same real numbers and actions as Settings → Data &
// Storage (storageInventory.ts, downloadData). The concept's separate "Export
// archive" row is folded into Download: there is one export, and two buttons
// for it would suggest two. Danger Zone keeps Delete account only: Deactivate is
// left off (owner ruling 2026-09-30) and changing a password is not a danger.
import { useState } from 'react';
import styled from 'styled-components';
import { Button, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useSession } from '@/hooks';
import { DeleteAccountDialog } from '../../DeleteAccountDialog';
import {
  clearInterfaceState,
  formatBytes,
  storageInventory,
  totalBytes,
} from '../storageInventory';
import { CONNECTED_PROVIDERS } from '../connectedProviders';
import { CardHead, ComingSoon, DashCard, DashLinkRow, DashRow } from '../../dashboard/DashCard';
import { LinkButton, RowList } from '../../dashboard/dashStyles';

export function DataStorageCard() {
  const { downloadData } = useSession();
  const toast = useToast();
  const [bytes, setBytes] = useState(() => totalBytes(storageInventory()));

  async function download() {
    await downloadData();
    toast('Your data is downloading');
  }

  function clear() {
    const removed = clearInterfaceState();
    setBytes(totalBytes(storageInventory()));
    toast(
      removed === 0
        ? 'Nothing to clear. Interface state is already empty'
        : `Cleared ${removed} stored ${removed === 1 ? 'item' : 'items'}`,
    );
  }

  return (
    <DashCard labelledBy="acct-data-title" testId="data-card">
      <CardHead icon="database" title="Data & Storage" titleId="acct-data-title" />
      <RowList>
        <DashRow
          icon="download"
          label="Download your data"
          hint="A JSON copy of everything you have put in, without your password"
        >
          <Button
            variant="secondary"
            size="sm"
            aria-label="Download your data"
            onClick={() => void download()}
          >
            Download
          </Button>
        </DashRow>
        <DashLinkRow
          to="/settings/data"
          icon="database"
          label="Storage on this device"
          value={formatBytes(bytes)}
          testId="storage-total-row"
        />
        <DashRow
          icon="trash"
          label="Clear interface state"
          hint="Forgets collapsed sidebars and dismissed banners. You stay signed in."
        >
          <Button variant="secondary" size="sm" aria-label="Clear interface state" onClick={clear}>
            Clear
          </Button>
        </DashRow>
      </RowList>
    </DashCard>
  );
}

const Tiles = styled.ul`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  /* Flows by the card's own width: three across when the card is wide, stacked
     when it is a single narrow column in the middle layout. */
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
`;

const Tile = styled.li`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surfaceRaised};
  min-width: 0;
`;

const TileHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  > svg {
    flex: none;
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const TileName = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const TileState = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function ConnectedCard() {
  return (
    <DashCard labelledBy="acct-connected-title" span={3} testId="connected-card">
      <CardHead
        icon="link"
        title="Connected Accounts"
        titleId="acct-connected-title"
        subtitle="Linking other accounts from Settings is coming soon"
        divided={false}
        aside={
          <LinkButton to="/settings/connected" $size="sm" aria-label="Manage connected accounts">
            Manage
          </LinkButton>
        }
      />
      <Tiles>
        {CONNECTED_PROVIDERS.filter((p) => p.onDashboard).map((p) => (
          <Tile key={p.id} data-testid={`connected-tile-${p.id}`}>
            <TileHead>
              <Icon name={p.icon} size={22} />
              <div>
                <TileName>{p.label}</TileName>
                <TileState>Not connected</TileState>
              </div>
            </TileHead>
            <div>
              <ComingSoon />
            </div>
          </Tile>
        ))}
      </Tiles>
    </DashCard>
  );
}

const DangerNote = styled.p`
  margin: 12px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function DangerZoneCard() {
  const [deleting, setDeleting] = useState(false);
  return (
    <DashCard labelledBy="acct-danger-title" span={3} tone="danger" testId="danger-zone">
      <CardHead
        icon="alert"
        title="Danger Zone"
        titleId="acct-danger-title"
        subtitle="This cannot be undone"
        tone="danger"
      />
      <RowList>
        <DashRow
          icon="trash"
          label="Delete account"
          hint="Permanently remove your account, your posts and everything else you have put in"
        >
          <Button variant="danger" size="sm" onClick={() => setDeleting(true)}>
            Delete account
          </Button>
        </DashRow>
      </RowList>
      <DangerNote>Want a copy first? Use Download your data above before you delete.</DangerNote>
      {deleting && <DeleteAccountDialog onClose={() => setDeleting(false)} />}
    </DashCard>
  );
}
